import React from 'react';
import PromptContainer, { PromptContainerProps } from './PromptContainer';
import Stack, { contentStackSpacing } from '../Stack';
import { View } from 'react-native';
import Text from '../Text';
import { useAssertHasNestedScrollViewPortal } from '../NestedScrollViewPortal';
import { mainContentPadding } from '../MainContentContainer';
import Icon, { IconName } from '../Icon';
import Divider from '../Divider';
import PromptActions, { PromptActionButtonProps } from './PromptActions';
import { getSpacing } from '../../../utils/spacing';
import { PRScrollView } from '../../shared/Scroll';

export type StandardPromptOwnProps = {
    title?: string;
    children: React.ReactNode;
    headerAction?: React.ReactNode;
    actions?: PromptActionButtonProps[];
    titleIcon?: IconName;
    innerTitle?: boolean;
    testID?: string;
    onClosed?: () => void;
} & Omit<PromptContainerProps, 'renderPromptContents' | 'mobileMaxHeight' | 'desktopMaxHeight'>;

export type StandardPromptProps = StandardPromptOwnProps;

const StandardPrompt: React.FC<StandardPromptProps> = ({
    title,
    titleIcon,
    children,
    headerAction,
    actions,
    innerTitle,
    testID,
    ...toPass
}) => {
    useAssertHasNestedScrollViewPortal();

    const maybeRenderWithHeader = (children: React.ReactNode) => {
        if (!title || innerTitle) return children;

        return (
            <Stack vertical fullWidth>
                <View>
                    <Stack
                        horizontal
                        justify="space-between"
                        align="center"
                        fullWidth
                        style={{ padding: mainContentPadding }}
                    >
                        <Stack testID={testID} horizontal align="center" space={2.5} flex={1}>
                            {titleIcon ? <Icon name={titleIcon} size="button-icon" /> : null}
                            <Text variant="h4" wrapText>
                                {title}
                            </Text>
                        </Stack>
                        <Stack horizontal flex={0}>
                            {headerAction}
                        </Stack>
                    </Stack>
                </View>
                <Divider horizontal />
                {children}
            </Stack>
        );
    };

    const maybeRenderInsetHeader = (children: React.ReactNode) => {
        if (!title || !innerTitle) return children;

        return (
            <>
                <Stack horizontal justify="center" align="center" fullWidth>
                    <Stack horizontal align="center" space={2.5} flex={1}>
                        {titleIcon ? <Icon name={titleIcon} size="button-icon" /> : null}
                        <Text variant="h4">{title}</Text>
                    </Stack>
                    <Stack horizontal>{headerAction}</Stack>
                </Stack>
                {children}
            </>
        );
    };

    return (
        <PromptContainer
            {...toPass}
            mobileMaxHeight={'80%'}
            renderPromptContents={() =>
                maybeRenderWithHeader(
                    <PRScrollView style={{ width: '100%' }}>
                        <Stack vertical fullWidth space={contentStackSpacing} padding={getSpacing(6)}>
                            {maybeRenderInsetHeader(
                                <>
                                    {children}
                                    {actions?.length ? (
                                        <View
                                            style={{
                                                width: '100%',
                                                marginTop: getSpacing(8),
                                                paddingBottom: getSpacing(10),
                                            }}
                                        >
                                            <PromptActions actions={actions} />
                                        </View>
                                    ) : null}
                                </>,
                            )}
                        </Stack>
                    </PRScrollView>,
                )
            }
        />
    );
};

export default React.memo(StandardPrompt);
